<template>
  <div>
    <VDropdown :shown="isOpen" placement="bottom-start" @update:shown="onDropdownToggle">
      <div
        class="is-flex is-align-items-center is-clickable has-text-weight-normal
          is-justify-content-space-between has-background-white multidate-col"
        @click="isOpen = !isOpen"
      >
        <div class="is-size-3 text-overflow text-clamp" v-tooltip="placeholder">
          {{ placeholder }}
        </div>
        <i class="icon-disclosearrow"></i>
      </div>

      <template #popper>
        <div class="card cf-number-card">
          <header class="card-header has-background-grey-lighter">
            <h4
              class="card-header-title is-size-3 has-text-black-bis
                is-capitalized has-text-weight-bold is-paddingless"
            >
              {{ rowField.customField.label }}
            </h4>
          </header>
          <section class="card-content is-marginless has-background-white">
            <div class="column">
                <div class="field">
                <field-generic-input
                    label="Min: "
                    labelPosition="is-left"
                    :value="minVal !== null ? String(minVal) : ''"
                    placeholder="e.g. 1"
                    class="has-text-weight-bold"
                    :isEditing="true"
                    type="number"
                    @update:value="(v) => minVal = v === '' ? null : Number(v)"
                />
                </div>
                <div class="field mt-3">
                <field-generic-input
                    label="Max: "
                    labelPosition="is-left"
                    :value="maxVal !== null ? String(maxVal) : ''"
                    placeholder="e.g. 100000"
                    class="has-text-weight-bold"
                    :isEditing="true"
                    type="number"
                    @update:value="(v) => maxVal = v === '' ? null : Number(v)"
                />
                </div>
                <p v-if="validationMsg" class="has-text-danger is-size-3 mt-2">
                {{ validationMsg }}
                </p>
            </div>
            </section>
          <footer
            class="card-footer is-justify-content-space-between has-background-grey-lighter"
          >
            <div>
              <button
                class="button is-outlined has-text-weight-bold"
                @click="clearAll"
              >
                Clear All
              </button>
            </div>
            <div class="buttons">
              <button
                class="button is-outlined is-danger has-text-weight-bold"
                @click="discard"
              >
                Discard
              </button>
              <button
                class="button has-text-weight-bold is-success has-text-white"
                :disabled="applyDisabled"
                @click="apply"
              >
                Apply
              </button>
            </div>
          </footer>
        </div>
      </template>
    </VDropdown>
  </div>
</template>

<script>
import { reactive, toRefs, computed, onBeforeMount,watch } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'CfNumberFilter',
  props: {
    rowField: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['filter-change'],
  setup(props, { emit }) {
    const store = useStore();

    const state = reactive({
      isOpen: false,
      minVal: null,
      maxVal: null,
    });

    onBeforeMount(() => {
      const existing = store.state.queryParams[props.rowField.id] || {};
      state.minVal = existing.min ?? null;
      state.maxVal = existing.max ?? null;
    });

    const validationMsg = computed(() => {
      if (state.minVal !== null && state.maxVal !== null && state.minVal > state.maxVal) {
        return 'Min cannot be greater than Max';
      }
      return '';
    });

    const applyDisabled = computed(() => {
      return (state.minVal === null && state.maxVal === null) || !!validationMsg.value;
    });

    const placeholder = computed(() => {
      if (state.minVal !== null || state.maxVal !== null) {
        const minLabel = state.minVal !== null ? state.minVal : '-';
        const maxLabel = state.maxVal !== null ? state.maxVal : '-';
        return `${minLabel}-${maxLabel}`;
      }
      return 'All Values';
    });

    const onDropdownToggle = (val) => {
      state.isOpen = val;
    };

    const commitToStore = () => {
      const payload = (state.minVal === null && state.maxVal === null) ? null : { min: state.minVal, max: state.maxVal };
      store.commit('setQueryParams', { [props.rowField.id]: payload });
      emit('filter-change');
    };

    const apply = () => {
      state.isOpen = false;
      commitToStore();
    };

    const discard = () => {
      const existing = store.state.queryParams[props.rowField.id] || {};
      state.minVal = existing.min ?? null;
      state.maxVal = existing.max ?? null;
      state.isOpen = false;
    };

    watch(
      () => store.state.queryParams[props.rowField.id],
      (newVal) => {
        if (!newVal || (newVal.min === null && newVal.max === null)) {
          state.minVal = null;
          state.maxVal = null;
        }
      }
    );

    const clearAll = () => {
      state.minVal = null;
      state.maxVal = null;
      state.isOpen = false;
      commitToStore();
    };

    return {
      ...toRefs(state),
      placeholder,
      validationMsg,
      applyDisabled,
      onDropdownToggle,
      apply,
      discard,
      clearAll,
    };
  },
};
</script>

<style scoped>
.cf-number-card {
  width: 340px;
  max-width: inherit;
}
.card-content {
  height: auto;
  min-height: 120px;
  padding: 16px;
}
</style>